import { useMemo, useCallback, useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridValueGetterParams,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
  GridPagination,
} from "@mui/x-data-grid";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Link,
  Snackbar,
  Alert,
} from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import FlashOffIcon from "@mui/icons-material/FlashOff";
import * as Sentry from "@sentry/react";
import { predictionManagerAxiosApi } from "src/Config/axiosApi";
import { RejectPairDialog } from "src/Modules/PredictionMapper/components/RejectPairDialog";
import { LIVE_STATUS_LIVE, MatchedByType, PredictionOpportunity } from "../../types";
import { usePredictions } from "../../context";
import { useTogglePairArchive } from "../../api/usePositions";
import { CATEGORY_LABELS, UNCATEGORIZED } from "../../constants/categories";

interface Props {
  data: PredictionOpportunity[];
  isLoading?: boolean;
  /**
   * Called with the agent run id when an unmatch dispatches a fix-match run.
   * The parent (PredictionsPage) owns the EventAgentRunDrawer so the same
   * drawer is shared with the toolbar's runs button.
   */
  onAgentRunStarted?: (runId: string) => void;
}

function CustomPagination(props: any) {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Box display="flex" alignItems="center">
      <IconButton
        onClick={() => apiRef.current.setPage(0)}
        disabled={page === 0}
      >
        <FirstPageIcon />
      </IconButton>
      <GridPagination {...props} />
      <IconButton
        onClick={() => apiRef.current.setPage(pageCount - 1)}
        disabled={page === pageCount - 1}
      >
        <LastPageIcon />
      </IconButton>
    </Box>
  );
}

const formatPercent = (value: number) => {
  if (value == null || isNaN(value)) return "-";
  return (value * 100).toFixed(2) + "%";
};

const formatDecimal = (value: number, digits = 4) => {
  if (value == null || isNaN(value)) return "-";
  return value.toFixed(digits);
};

const platformLabel = (platform: string) => {
  const map: Record<string, string> = {
    polymarket: "Poly",
    kalshi: "Kalshi",
  };
  return map[platform] || platform;
};

const platformFullLabel = (platform: string) => {
  const map: Record<string, string> = {
    polymarket: "Polymarket",
    kalshi: "Kalshi",
  };
  return map[platform] || platform;
};

// Per-platform expiry can arrive as a plain "YYYY-MM-DD" or a full ISO
// timestamp — show just the date portion, fall back to the raw value.
const formatExpiryDate = (value?: string) => {
  if (!value) return "unknown";
  const datePart = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : value;
};

const matchColor = (matchedBy: string): "success" | "info" | "warning" | "secondary" | "default" => {
  switch (matchedBy) {
    case "manual":
      return "success";
    case "ai_confirmed":
    case "keyword_confirmed":
      return "info";
    case "ai_pending":
    case "keyword_pending":
      return "warning";
    default:
      return "default";
  }
};

const matchLabel = (matchedBy: string): string => {
  const labels: Record<string, string> = {
    manual: "manual",
    ai_confirmed: "ai",
    ai_pending: "ai?",
    keyword_pending: "kw?",
    keyword_confirmed: "kw",
    semantic_fallback: "sem",
    semantic_confirmed: "sem",
    structured_match: "struct",
    structured_confirmed: "struct",
  };
  return labels[matchedBy] || matchedBy;
};

const confirmedMatchedBy = (current: MatchedByType): MatchedByType => {
  if (current === "ai_pending") return "ai_confirmed";
  if (current === "keyword_pending") return "keyword_confirmed";
  return current;
};

export const PredictionsTable = ({ data, isLoading, onAgentRunStarted }: Props) => {
  // Reason-gated unmatch: clicking the unlink icon opens RejectPairDialog. An
  // empty reason is a plain unmatch; a reason also dispatches a fix-match run
  // (delivered via `onAgentRunStarted` so the page-level drawer opens on it).
  const [rejectTarget, setRejectTarget] = useState<{ id: string; name: string } | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({ open: false, message: "", severity: "success" });
  const { removePair, confirmPair } = usePredictions();
  const { mutate: toggleArchive } = useTogglePairArchive();
  const [archivingId, setArchivingId] = useState<string | null>(null);

  const handleToggleArchive = useCallback((pairId: string, currentArchived: boolean) => {
    setArchivingId(pairId);
    toggleArchive(
      { pairId, archived: !currentArchived },
      {
        onSuccess: () => {
          setArchivingId(null);
          setSnackbar({
            open: true,
            message: currentArchived ? "Restored to Active" : "Archived",
            severity: "success",
          });
        },
        onError: () => {
          setArchivingId(null);
          setSnackbar({ open: true, message: "Failed to toggle archive", severity: "error" });
        },
      }
    );
  }, [toggleArchive]);

  const handleUnmatch = useCallback((pairId: string, pairName: string) => {
    setRejectTarget({ id: pairId, name: pairName });
  }, []);

  const handleConfirm = useCallback(async (pairId: string, currentMatchedBy: MatchedByType) => {
    setConfirmingId(pairId);
    try {
      await predictionManagerAxiosApi.patch(`/pairs/${pairId}/confirm`);
      const newMatchedBy = confirmedMatchedBy(currentMatchedBy);
      confirmPair(pairId, newMatchedBy);
      setSnackbar({ open: true, message: "Match confirmed", severity: "success" });
    } catch (err) {
      console.error("Failed to confirm pair:", err);
      setSnackbar({ open: true, message: "Failed to confirm match", severity: "error" });
    } finally {
      setConfirmingId(null);
    }
  }, [confirmPair]);

  // Operator promotion to `matchedBy: 'manual'` — separate from
  // `handleConfirm` (which only promotes pending AI/keyword matches to
  // their `_confirmed` variant). This is the explicit "I trust this
  // pair enough to commit capital" verdict, gating eligibility for the
  // position-opener. Confirms via a browser dialog because the action
  // is irreversible in practice (no UI to un-manual a pair).
  const [manualConfirmingId, setManualConfirmingId] = useState<string | null>(null);
  const handleManualConfirm = useCallback(async (pairId: string, pairName: string) => {
    if (!confirm(`Mark "${pairName}" as manually confirmed?\n\nThis makes it eligible for the position-opener.`)) return;
    setManualConfirmingId(pairId);
    try {
      await predictionManagerAxiosApi.patch(`/pairs/${pairId}/manual-confirm`);
      confirmPair(pairId, "manual" as MatchedByType);
      setSnackbar({ open: true, message: "Marked as manually confirmed", severity: "success" });
    } catch (err) {
      console.error("Failed to manually confirm pair:", err);
      setSnackbar({ open: true, message: "Failed to mark as manually confirmed", severity: "error" });
    } finally {
      setManualConfirmingId(null);
    }
  }, [confirmPair]);

  const [togglingPolarityId, setTogglingPolarityId] = useState<string | null>(null);

  const handleTogglePolarity = useCallback(async (pairId: string, currentValue: boolean) => {
    setTogglingPolarityId(pairId);
    try {
      await predictionManagerAxiosApi.patch(`/pairs/${pairId}`, { polarityInverted: !currentValue });
      setSnackbar({ open: true, message: `Polarity ${!currentValue ? "inverted" : "restored"}`, severity: "success" });
    } catch (err) {
      console.error("Failed to toggle polarity:", err);
      setSnackbar({ open: true, message: "Failed to toggle polarity", severity: "error" });
    } finally {
      setTogglingPolarityId(null);
    }
  }, []);

  // Force-live applies a 4h operator override; the backend caps at 24h. Clear
  // removes any active override AND re-runs the auto classifier so the pair
  // lands wherever the rules say — see live-events skill. The buttons are a
  // pure toggle: shown on every row, enabled state tracks pair.liveStatus.
  const [togglingLiveId, setTogglingLiveId] = useState<string | null>(null);
  const handleForceLive = useCallback(async (pairId: string) => {
    setTogglingLiveId(pairId);
    try {
      await predictionManagerAxiosApi.patch(
        `/live-events/pairs/${pairId}/force-live`,
        { durationMinutes: 240 }
      );
      setSnackbar({
        open: true,
        message: "Pair forced live for 4h",
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to force live:", err);
      setSnackbar({ open: true, message: "Failed to force live", severity: "error" });
    } finally {
      setTogglingLiveId(null);
    }
  }, []);

  const handleClearForceLive = useCallback(async (pairId: string) => {
    setTogglingLiveId(pairId);
    try {
      await predictionManagerAxiosApi.delete(
        `/live-events/pairs/${pairId}/force-live`
      );
      setSnackbar({
        open: true,
        message: "Override cleared — auto classifier re-applied",
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to clear force-live:", err);
      setSnackbar({ open: true, message: "Failed to clear override", severity: "error" });
    } finally {
      setTogglingLiveId(null);
    }
  }, []);

  const columns: GridColDef<PredictionOpportunity>[] = useMemo(
    () => [
      {
        headerName: "Event",
        field: "pairName",
        type: "string",
        flex: 1,
        minWidth: 140,
        renderCell: ({ row }) => {
          let yesTitle = row.direction?.yesTitle;
          let noTitle = row.direction?.noTitle;
          // Fallback: split pairName "polymarket_title ↔ kalshi_title" and map by platform.
          // This should no longer fire once the backfill migration runs — report to Sentry so
          // remaining cases are observable and can be hunted down.
          if (!yesTitle || !noTitle) {
            Sentry.captureMessage("prediction.title_fallback.opportunities", {
              level: "warning",
              extra: {
                pairId: row.pairId,
                pairName: row.pairName,
                yesPlatform: row.direction?.yesPlatform,
                noPlatform: row.direction?.noPlatform,
                missing: !yesTitle && !noTitle ? "both" : !yesTitle ? "yes" : "no",
              },
            });
            const parts = row.pairName.split("↔").map((s: string) => s.trim());
            const polyTitle = parts[0] || row.pairName;
            const kalshiTitle = parts[1] || polyTitle;
            const yesPlatform = row.direction?.yesPlatform;
            yesTitle = yesTitle || (yesPlatform === "polymarket" ? polyTitle : kalshiTitle);
            noTitle = noTitle || (yesPlatform === "polymarket" ? kalshiTitle : polyTitle);
          }
          const yesUrl = row.direction?.yesMarketUrl;
          const noUrl = row.direction?.noMarketUrl;
          const yesPlatformLbl = platformLabel(row.direction?.yesPlatform);
          const noPlatformLbl = platformLabel(row.direction?.noPlatform);
          const categoryKey = row.category ?? UNCATEGORIZED;
          const categoryLabel =
            CATEGORY_LABELS[categoryKey as keyof typeof CATEGORY_LABELS] ??
            categoryKey;
          return (
            <Tooltip title={`${yesPlatformLbl}: ${yesTitle}\n${noPlatformLbl}: ${noTitle}`} placement="bottom-start">
              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", py: 0.5, overflow: "hidden" }}>
                <Box sx={{ mb: 0.25 }}>
                  <Chip
                    size="small"
                    label={categoryLabel}
                    variant="outlined"
                    sx={{
                      height: 16,
                      fontSize: "0.55rem",
                      borderColor: row.category ? "primary.main" : "rgba(255,255,255,0.2)",
                      color: row.category ? "primary.light" : "text.disabled",
                      "& .MuiChip-label": { px: 0.75 }
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {yesUrl ? (
                    <Link
                      href={yesUrl}
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ fontSize: "0.65rem", fontWeight: 600, color: "text.secondary", minWidth: 32, cursor: "pointer" }}
                    >
                      {yesPlatformLbl}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: "0.65rem", fontWeight: 600, color: "text.secondary", minWidth: 32 }}>
                      {yesPlatformLbl}
                    </Typography>
                  )}
                  <Typography variant="body2" noWrap sx={{ fontSize: "0.65rem", lineHeight: 1.2 }}>
                    {yesTitle}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {noUrl ? (
                    <Link
                      href={noUrl}
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ fontSize: "0.65rem", fontWeight: 600, color: "text.secondary", minWidth: 32, cursor: "pointer" }}
                    >
                      {noPlatformLbl}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: "0.65rem", fontWeight: 600, color: "text.secondary", minWidth: 32 }}>
                      {noPlatformLbl}
                    </Typography>
                  )}
                  <Typography variant="body2" noWrap sx={{ fontSize: "0.65rem", lineHeight: 1.2, color: "text.secondary" }}>
                    {noTitle}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        headerName: "YES @",
        field: "direction.yesPlatform",
        type: "string",
        width: 60,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams) => params.row.direction?.yesPlatform,
        renderCell: ({ row, value }) => {
          const url = row.direction?.yesMarketUrl;
          const img = (
            <img
              src={`/assets/exchanges/png/100x100/${value}.png`}
              alt={value}
              style={{ width: 24, height: 24, borderRadius: "50%" }}
            />
          );
          return (
            <Tooltip title={platformLabel(value)} placement="top">
              {url ? (
                <a href={url} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>
                  {img}
                </a>
              ) : img}
            </Tooltip>
          );
        },
      },
      {
        headerName: "NO @",
        field: "direction.noPlatform",
        type: "string",
        width: 60,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams) => params.row.direction?.noPlatform,
        renderCell: ({ row, value }) => {
          const url = row.direction?.noMarketUrl;
          const img = (
            <img
              src={`/assets/exchanges/png/100x100/${value}.png`}
              alt={value}
              style={{ width: 24, height: 24, borderRadius: "50%" }}
            />
          );
          return (
            <Tooltip title={platformLabel(value)} placement="top">
              {url ? (
                <a href={url} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>
                  {img}
                </a>
              ) : img}
            </Tooltip>
          );
        },
      },
      {
        headerName: "Ask YES",
        field: "direction.yesAsk",
        type: "number",
        width: 80,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams) => params.row.direction?.yesAsk,
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
            {formatDecimal(value)}
          </Typography>
        ),
      },
      {
        headerName: "Ask NO",
        field: "direction.noAsk",
        type: "number",
        width: 80,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams) => params.row.direction?.noAsk,
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
            {formatDecimal(value)}
          </Typography>
        ),
      },
      {
        headerName: "Cost",
        field: "cost",
        type: "number",
        width: 80,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              color: value < 0.95 ? "#4caf50" : value < 0.99 ? "#ff9800" : "inherit",
              fontWeight: value < 0.95 ? 700 : 400,
            }}
          >
            {formatDecimal(value)}
          </Typography>
        ),
      },
      {
        headerName: "Profit %",
        field: "profitPct",
        type: "number",
        width: 85,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              color: "#4caf50",
              fontWeight: 600,
            }}
          >
            {formatPercent(value)}
          </Typography>
        ),
      },
      {
        headerName: "Days",
        field: "daysToExpiry",
        type: "number",
        width: 60,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value, row }: any) => {
          if (value == null) return '';
          const display = value < 1 ? value.toFixed(1) : Math.round(value);
          const dir = row?.direction;
          if (!dir) return display;
          const tooltip = `${platformFullLabel(dir.yesPlatform)}: ${formatExpiryDate(
            dir.yesExpiryDate,
          )}\n${platformFullLabel(dir.noPlatform)}: ${formatExpiryDate(
            dir.noExpiryDate,
          )}`;
          return (
            <Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{tooltip}</Box>} placement="top">
              <span>{display}</span>
            </Tooltip>
          );
        },
      },
      {
        headerName: "Maker TNA",
        field: "makerTna",
        type: "number",
        width: 95,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              fontWeight: 700,
              color: value > 0.5 ? "#4caf50" : value > 0.2 ? "#ff9800" : "inherit",
            }}
          >
            {formatPercent(value)}
          </Typography>
        ),
      },
      {
        headerName: "Taker TNA",
        field: "takerTna",
        type: "number",
        width: 95,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              fontWeight: 700,
              color: value > 0.5 ? "#4caf50" : value > 0.2 ? "#ff9800" : "inherit",
            }}
          >
            {formatPercent(value)}
          </Typography>
        ),
      },
      {
        headerName: "Liquidity",
        field: "maxFillableContracts",
        type: "number",
        width: 90,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
            {value == null || isNaN(value) ? "-" : Math.floor(value).toLocaleString()}
          </Typography>
        ),
      },
      {
        headerName: "Match",
        field: "matchConfidence",
        type: "number",
        width: 120,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          const tooltipText = row.keywordOverlap?.length
            ? `Keywords: ${row.keywordOverlap.join(", ")}`
            : row.matchedBy;
          const confidenceText = row.matchConfidence != null
            ? `${Math.round(row.matchConfidence * 100)}%`
            : null;

          return (
            <Tooltip title={tooltipText} placement="top">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Chip
                  label={matchLabel(row.matchedBy)}
                  size="small"
                  color={matchColor(row.matchedBy) as any}
                  sx={{ fontSize: "0.65rem" }}
                />
                {confidenceText && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      color: row.matchConfidence! >= 0.8 ? "success.main" : row.matchConfidence! >= 0.5 ? "warning.main" : "error.main",
                      fontWeight: 600,
                    }}
                  >
                    {confidenceText}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        headerName: "",
        field: "actions",
        type: "string",
        width: 138,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const isConfirming = confirmingId === row.pairId;
          const isArchiving = archivingId === row.pairId;
          const isManualConfirming = manualConfirmingId === row.pairId;
          const isPending = row.matchedBy === "ai_pending" || row.matchedBy === "keyword_pending";
          const isAlreadyManual = row.matchedBy === "manual";
          return (
            <Box sx={{ display: "flex", gap: 0 }}>
              {isPending && (
                <Tooltip title="Confirm match" placement="top">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleConfirm(row.pairId, row.matchedBy)}
                      disabled={isConfirming}
                      sx={{ color: "success.main", p: 0.25 }}
                    >
                      {isConfirming ? (
                        <CircularProgress size={16} />
                      ) : (
                        <CheckCircleOutlineIcon sx={{ fontSize: "1rem" }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {!isAlreadyManual && (
                <Tooltip title="Mark as manually confirmed (makes pair eligible for the position-opener)" placement="top">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleManualConfirm(row.pairId, row.pairName)}
                      disabled={isManualConfirming}
                      sx={{ color: "success.dark", p: 0.25 }}
                    >
                      {isManualConfirming ? (
                        <CircularProgress size={16} />
                      ) : (
                        <VerifiedOutlinedIcon sx={{ fontSize: "1rem" }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={row.polarityInverted ? "Remove polarity inversion" : "Invert polarity (swap YES/NO)"} placement="top">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleTogglePolarity(row.pairId, !!row.polarityInverted)}
                    disabled={togglingPolarityId === row.pairId}
                    sx={{ color: row.polarityInverted ? "secondary.main" : "grey.500", p: 0.25 }}
                  >
                    {togglingPolarityId === row.pairId ? (
                      <CircularProgress size={16} />
                    ) : (
                      <SwapHorizIcon sx={{ fontSize: "1rem" }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              {row.liveStatus === LIVE_STATUS_LIVE ? (
                <Tooltip
                  title="Clear live override (re-runs auto classifier)"
                  placement="top"
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleClearForceLive(row.pairId)}
                      disabled={togglingLiveId === row.pairId}
                      sx={{ color: "warning.main", p: 0.25 }}
                    >
                      {togglingLiveId === row.pairId ? (
                        <CircularProgress size={16} />
                      ) : (
                        <FlashOffIcon sx={{ fontSize: "1rem" }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip
                  title="Force into Live pool for 4h"
                  placement="top"
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleForceLive(row.pairId)}
                      disabled={togglingLiveId === row.pairId}
                      sx={{ color: "grey.500", p: 0.25 }}
                    >
                      {togglingLiveId === row.pairId ? (
                        <CircularProgress size={16} />
                      ) : (
                        <FlashOnIcon sx={{ fontSize: "1rem" }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={row.archived ? "Restore to Active" : "Archive (move to Archived tab)"} placement="top">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleToggleArchive(row.pairId, !!row.archived)}
                    disabled={isArchiving}
                    sx={{ color: row.archived ? "warning.main" : "grey.500", p: 0.25 }}
                  >
                    {isArchiving ? (
                      <CircularProgress size={16} />
                    ) : row.archived ? (
                      <UnarchiveOutlinedIcon sx={{ fontSize: "1rem" }} />
                    ) : (
                      <ArchiveOutlinedIcon sx={{ fontSize: "1rem" }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Unmatch pair" placement="top">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleUnmatch(row.pairId, row.pairName)}
                    sx={{ color: "grey.500", p: 0.25 }}
                  >
                    <LinkOffIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [confirmingId, manualConfirmingId, togglingPolarityId, archivingId, togglingLiveId, handleUnmatch, handleConfirm, handleManualConfirm, handleTogglePolarity, handleToggleArchive, handleForceLive, handleClearForceLive]
  );

  return (
    <>
    <DataGrid
      rows={data}
      columns={columns}
      getRowId={(row) => row.pairId}
      getRowClassName={(params) => {
        const classes: string[] = [];
        if (params.row.polarityInverted) classes.push("polarity-inverted");
        return classes.join(" ");
      }}
      loading={isLoading}
      autoHeight
      rowHeight={52}
      disableSelectionOnClick
      initialState={{
        sorting: {
          sortModel: [
          { field: "matchConfidence", sort: "desc" },
          { field: "takerTna", sort: "desc" },
        ],
        },
        pagination: {
          pageSize: 25,
        },
      }}
      rowsPerPageOptions={[10, 25, 50, 100]}
      components={{
        Pagination: CustomPagination,
      }}
      sx={{
        border: "none",
        "& .MuiDataGrid-cell": {
          borderBottom: "1px solid rgba(255,255,255,0.05)",
        },
        "& .MuiDataGrid-columnHeaders": {
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        },
        "& .MuiDataGrid-row:hover": {
          backgroundColor: "rgba(255,255,255,0.03)",
        },
        "& .polarity-inverted": {
          backgroundColor: "rgba(138, 43, 226, 0.08)",
        },
        "& .polarity-inverted:hover": {
          backgroundColor: "rgba(138, 43, 226, 0.15) !important",
        },
      }}
    />
    <Snackbar
      open={snackbar.open}
      autoHideDuration={3000}
      onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>
        {snackbar.message}
      </Alert>
    </Snackbar>
    <RejectPairDialog
      pairId={rejectTarget?.id ?? null}
      pairName={rejectTarget?.name}
      onClose={() => setRejectTarget(null)}
      onUnmatched={(id) => {
        removePair(id);
        setSnackbar({ open: true, message: "Pair unmatched", severity: "success" });
      }}
      onFixMatchStarted={(runId) => onAgentRunStarted?.(runId)}
      onError={(err) =>
        setSnackbar({
          open: true,
          message: `Unmatch failed: ${(err as any)?.message ?? "unknown error"}`,
          severity: "error",
        })
      }
    />
    </>
  );
};
